import { GRASS_CHARACTERISTICS } from '@/data/grass-characteristics'
import type {
  CanonicalLawnProfile,
  CanonicalZoneProfile,
  ConfidenceLevel,
  GrassTypeResolution,
  InputEffect,
  LawnSizeResolution,
  PlanConfidence,
  SunlightBand,
  SunlightResolution,
} from './types'

const SOURCE_GRASS = ['okstate-shade-2026', 'uf-ifas-shade-2026', 'missouri-shade-2026']
const SOURCE_SUN = ['okstate-shade-2026', 'uf-ifas-shade-2026', 'missouri-shade-2026']
const SOURCE_SIZE = ['rutgers-fertilizer-math-2026']

const CANONICAL_GRASS_KEYS = Object.keys(GRASS_CHARACTERISTICS)
const CANONICAL_BY_TOKEN = new Map(CANONICAL_GRASS_KEYS.map((key) => [tokenize(key), key]))

const GRASS_ALIASES: Record<string, string> = {
  bermuda: 'Bermudagrass',
  bermudagrass: 'Bermudagrass',
  bermudagrasscommon: 'Bermudagrass',
  commonbermuda: 'Bermudagrass',
  commonbermudagrass: 'Bermudagrass',
  hybridbermuda: 'Bermudagrass',
  hybridbermudagrass: 'Bermudagrass',
  zoysia: 'Zoysiagrass',
  zoysiagrass: 'Zoysiagrass',
  staugustine: 'St. Augustinegrass',
  staugustinegrass: 'St. Augustinegrass',
  saintaugustine: 'St. Augustinegrass',
  saintaugustinegrass: 'St. Augustinegrass',
  centipede: 'Centipede',
  centipedegrass: 'Centipede',
  bahia: 'Bahiagrass',
  bahiagrass: 'Bahiagrass',
  buffalograss: 'Buffalograss',
  buffalo: 'Buffalograss',
  kentuckyblue: 'Kentucky Bluegrass',
  kentuckybluegrass: 'Kentucky Bluegrass',
  bluegrass: 'Kentucky Bluegrass',
  kbg: 'Kentucky Bluegrass',
  perennialrye: 'Perennial Ryegrass',
  perennialryegrass: 'Perennial Ryegrass',
  ryegrass: 'Perennial Ryegrass',
  tallfescue: 'Tall Fescue',
  turftypetallfescue: 'Tall Fescue',
  tttf: 'Tall Fescue',
  finefescue: 'Fine Fescue',
  kikuyu: 'Kikuyugrass',
  kikuyugrass: 'Kikuyugrass',
  kikuyograss: 'Kikuyugrass',
  carpetgrass: 'Carpetgrass',
  commoncarpetgrass: 'Carpetgrass',
  axonopusfissifolius: 'Carpetgrass',
  axonopusaffinis: 'Carpetgrass',
  annualrye: 'Annual Ryegrass',
  annualryegrass: 'Annual Ryegrass',
  italianrye: 'Annual Ryegrass',
  italianryegrass: 'Annual Ryegrass',
  winterrye: 'Annual Ryegrass',
  winterryegrass: 'Annual Ryegrass',
  coolseasonmix: 'Mixed Cool-Season Blend',
  coolseasonblend: 'Mixed Cool-Season Blend',
  mixedcoolseason: 'Mixed Cool-Season Blend',
  mixedcoolseasonblend: 'Mixed Cool-Season Blend',
}

// Ambiguous inputs. `alternatives` is what the UI offers to disambiguate; an
// optional `default` species is used as the canonical so the plan is real (not a
// generic fallback) while `ambiguous: true` keeps the "confirm your grass" nudge.
// 'fescue' defaults to Tall Fescue (the dominant lawn fescue); truly-unknown
// 'mixed' has no default and stays 'Not Sure' → starter plan.
const AMBIGUOUS_GRASS: Record<string, { alternatives: string[]; default?: string }> = {
  fescue: { alternatives: ['Tall Fescue', 'Fine Fescue'], default: 'Tall Fescue' },
  fescues: { alternatives: ['Tall Fescue', 'Fine Fescue'], default: 'Tall Fescue' },
  mixed: { alternatives: CANONICAL_GRASS_KEYS },
  mixedgrass: { alternatives: CANONICAL_GRASS_KEYS },
}

const BUCKET_SQFT: Record<string, number> = {
  small: 1500,
  medium: 3500,
  large: 7500,
  xl: 12000,
  xlarge: 12000,
  extralarge: 12000,
}

export function tokenize(raw: string): string {
  return raw
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '')
}

function compactWhitespace(raw?: string | null): string | null {
  const value = (raw ?? '').trim().replace(/\s+/g, ' ')
  return value || null
}

export function normalizeGrassType(raw?: string | null): GrassTypeResolution {
  const trimmed = compactWhitespace(raw)
  const token = trimmed ? tokenize(trimmed) : ''

  if (!token || token === 'notsure' || token === 'unknown' || token === 'unknownother') {
    return {
      raw: trimmed,
      canonical: 'Not Sure',
      isKnown: false,
      ambiguous: false,
      alternatives: [],
      confidence: 'low',
      sourceIds: SOURCE_GRASS,
    }
  }

  const direct = CANONICAL_BY_TOKEN.get(token)
  if (direct) {
    return {
      raw: trimmed,
      canonical: direct,
      isKnown: true,
      ambiguous: false,
      alternatives: [],
      confidence: 'high',
      sourceIds: SOURCE_GRASS,
    }
  }

  const alias = GRASS_ALIASES[token]
  if (alias) {
    return {
      raw: trimmed,
      canonical: alias,
      isKnown: true,
      ambiguous: false,
      alternatives: [],
      confidence: 'medium',
      sourceIds: SOURCE_GRASS,
    }
  }

  const ambiguous = AMBIGUOUS_GRASS[token]
  if (ambiguous) {
    return {
      raw: trimmed,
      canonical: ambiguous.default ?? 'Not Sure',
      isKnown: false,
      ambiguous: true,
      alternatives: ambiguous.alternatives,
      confidence: 'low',
      sourceIds: SOURCE_GRASS,
    }
  }

  return {
    raw: trimmed,
    canonical: 'Not Sure',
    isKnown: false,
    ambiguous: false,
    alternatives: [],
    confidence: 'low',
    sourceIds: SOURCE_GRASS,
  }
}

export function canonicalGrassType(raw?: string | null): string {
  return normalizeGrassType(raw).canonical
}

export function normalizeSunlight(raw?: string | null): SunlightResolution {
  const trimmed = compactWhitespace(raw)
  const token = trimmed ? tokenize(trimmed) : ''
  const canonical =
    token === 'full' || token === 'fullsun' || token === 'sun' || token === 'sunny'
      ? 'full_sun'
      : token === 'partial' || token === 'partialsun' || token === 'partshade' || token === 'partialshade'
        ? 'partial_sun'
        : token === 'shade' || token === 'mostlyshade' || token === 'heavyshade' || token === 'shady'
          ? 'shade'
          : null

  return {
    raw: trimmed,
    canonical,
    confidence: canonical ? 'high' : 'low',
    sourceIds: SOURCE_SUN,
  }
}

export function parseLawnSizeSqft(raw?: string | number | null): LawnSizeResolution {
  if (raw == null || raw === '') {
    return { raw: null, sqft: null, source: 'missing', confidence: 'low', sourceIds: SOURCE_SIZE }
  }

  const rawString = String(raw).trim()
  if (!rawString) {
    return { raw: null, sqft: null, source: 'missing', confidence: 'low', sourceIds: SOURCE_SIZE }
  }

  if (typeof raw === 'number' && Number.isFinite(raw) && raw > 0) {
    return { raw: rawString, sqft: Math.round(raw), source: 'exact', confidence: 'high', sourceIds: SOURCE_SIZE }
  }

  const bucket = BUCKET_SQFT[tokenize(rawString)]
  if (bucket) {
    return { raw: rawString, sqft: bucket, source: 'bucket', confidence: 'medium', sourceIds: SOURCE_SIZE }
  }

  const normalized = rawString.toLowerCase().replace(/,/g, '')
  const kMatch = normalized.match(/(\d+(?:\.\d+)?)\s*k\b/)
  if (kMatch) {
    return {
      raw: rawString,
      sqft: Math.round(Number(kMatch[1]) * 1000),
      source: 'exact',
      confidence: 'high',
      sourceIds: SOURCE_SIZE,
    }
  }

  const numberMatch = normalized.match(/\d+(?:\.\d+)?/)
  const n = numberMatch ? Number(numberMatch[0]) : Number.NaN
  if (Number.isFinite(n) && n > 0) {
    return { raw: rawString, sqft: Math.round(n), source: 'exact', confidence: 'high', sourceIds: SOURCE_SIZE }
  }

  return { raw: rawString, sqft: null, source: 'missing', confidence: 'low', sourceIds: SOURCE_SIZE }
}

export function zoneAreaSqft(zone: { area_override_sqft?: unknown; area_sqft?: unknown }): number | null {
  const parsed = parseLawnSizeSqft((zone.area_override_sqft ?? zone.area_sqft) as string | number | null)
  return parsed.sqft
}

export function resolveLawnSizeSqft(
  raw?: string | number | null,
  zones?: Array<{ area_override_sqft?: unknown; area_sqft?: unknown; is_enabled?: boolean | null }> | null,
): LawnSizeResolution {
  const parsed = parseLawnSizeSqft(raw)
  if (parsed.source === 'exact') return parsed

  const zoneTotal = (zones ?? [])
    .filter((zone) => zone.is_enabled !== false)
    .reduce((sum, zone) => sum + (zoneAreaSqft(zone) ?? 0), 0)

  if (zoneTotal > 0) {
    return {
      raw: parsed.raw,
      sqft: Math.round(zoneTotal),
      source: 'zone_total',
      confidence: 'medium',
      sourceIds: SOURCE_SIZE,
    }
  }

  return parsed
}

export function normalizeLawnProfile(input: {
  lawn: {
    id: string
    grass_type?: string | null
    sunlight?: string | null
    lawn_size?: string | number | null
  }
  zones?: Array<{
    id?: string | null
    name?: string | null
    grass_type?: string | null
    sunlight?: string | null
    soil_type?: string | null
    area_sqft?: string | number | null
    area_override_sqft?: string | number | null
    is_enabled?: boolean | null
  }> | null
}): CanonicalLawnProfile {
  const grassTypeResolution = normalizeGrassType(input.lawn.grass_type)
  const sunlightResolution = normalizeSunlight(input.lawn.sunlight)
  const lawnSizeResolution = resolveLawnSizeSqft(input.lawn.lawn_size, input.zones)
  const zones: CanonicalZoneProfile[] = (input.zones ?? []).map((zone) => {
    const zoneGrass = normalizeGrassType(zone.grass_type ?? input.lawn.grass_type)
    const zoneSun = normalizeSunlight(zone.sunlight ?? input.lawn.sunlight)
    return {
      id: zone.id ?? null,
      name: zone.name ?? null,
      grassType: zoneGrass.canonical,
      grassTypeRaw: compactWhitespace(zone.grass_type ?? input.lawn.grass_type),
      sunlight: zoneSun.canonical,
      areaSqft: zoneAreaSqft(zone),
      soilType: zone.soil_type ?? null,
      isEnabled: zone.is_enabled !== false,
    }
  })
  const zoneArea = zones.filter((zone) => zone.isEnabled).reduce((sum, zone) => sum + (zone.areaSqft ?? 0), 0)

  return {
    id: input.lawn.id,
    grassType: grassTypeResolution.canonical,
    grassTypeRaw: compactWhitespace(input.lawn.grass_type),
    grassTypeResolution,
    sunlight: sunlightResolution.canonical,
    sunlightRaw: compactWhitespace(input.lawn.sunlight),
    sunlightResolution,
    lawnSizeSqft: lawnSizeResolution.sqft,
    lawnSizeRaw: input.lawn.lawn_size == null ? null : String(input.lawn.lawn_size),
    lawnSizeResolution,
    zones,
    zoneAreaSqft: zoneArea > 0 ? Math.round(zoneArea) : null,
  }
}

function boolConfidence(value: boolean): ConfidenceLevel {
  return value ? 'high' : 'low'
}

const CONFIDENCE_RANK: Record<ConfidenceLevel, number> = { high: 3, medium: 2, low: 1 }

/** Lower of two confidence levels (never raises). */
function capConfidence(current: ConfidenceLevel, ceiling: ConfidenceLevel): ConfidenceLevel {
  return CONFIDENCE_RANK[ceiling] < CONFIDENCE_RANK[current] ? ceiling : current
}

export function buildPlanConfidence(input: {
  profile: CanonicalLawnProfile
  hasWeather: boolean
  hasHistory: boolean
  /**
   * Optional — when provided, an agronomic-mismatch effect downgrades the
   * affected confidence so a fundamentally mismatched site (e.g. a shade-intolerant
   * grass in full shade) no longer reports high confidence. Param stays optional so
   * the 6 existing callers compile unchanged; only the overhaul sites pass it.
   */
  inputEffects?: InputEffect[]
}): PlanConfidence {
  const { profile } = input
  const confidence: PlanConfidence = {
    grassType: profile.grassTypeResolution.confidence,
    lawnSize: profile.lawnSizeResolution.confidence,
    sunlight: profile.sunlightResolution.confidence,
    zones: profile.zones.length > 0 ? 'medium' : 'low',
    weather: boolConfidence(input.hasWeather),
    history: boolConfidence(input.hasHistory),
  }

  // Grass/site mismatch: a shade-intolerant grass planted in shade is a site
  // problem no product fixes, so we must not advertise high confidence in the
  // sunlight (and grass) dimensions. The effect's own confidence sets the ceiling:
  // 'high' (full shade) caps at 'low', 'medium' (partial) caps at 'medium'.
  const mismatch = input.inputEffects?.find((e) => e.kind === 'grass_shade_mismatch')
  if (mismatch) {
    const ceiling: ConfidenceLevel = mismatch.confidence === 'high' ? 'low' : 'medium'
    confidence.sunlight = capConfidence(confidence.sunlight, ceiling)
    confidence.grassType = capConfidence(confidence.grassType, ceiling)
  }

  return confidence
}

export function canonicalGrassKeys(): string[] {
  return [...CANONICAL_GRASS_KEYS]
}

export function grassAliases(): Record<string, string> {
  return { ...GRASS_ALIASES }
}
